import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTypedTranslation } from '@app/hooks';
import { IVaultDetail, TranslationsTypes, VaultSearchItem, WhereFoundItem } from '@app/models';
import { IModalData, ModalComponent, PasslyButton, PasslyInfiniteScroll } from '@app/components';
import { EmptyTable, EmptyTableType } from '@app/componentsV2';
import ScreenLockPortraitIcon from '@material-ui/icons/ScreenLockPortrait';
import VpnLockIcon from '@material-ui/icons/VpnLock';
import { FolderOutlined } from '@material-ui/icons';
import VpnKeyIcon from '@material-ui/icons/VpnKey';
import { getModalSwitch } from '@app/store/actions';
import { PasswordDetailsDialog } from '@app/pages/password/dialogs/passwordDetails.dialog';
import { basicService } from '@app/services';

interface IProps {
    rows: any[];
    totalCount?: number;
    currentCount?: number;
    onFetch: Function;
    resetLocal?: number;
    hasDoneASearch?: boolean;
    searchString?: string;
}

interface IVaultsFolderData {
    vaultFolderId: string;
    folderName: string;
    passwords: VaultSearchItem[];
}

const fieldsToFindSearched = [
    'passwordDescription',
    'passwordDomain',
    'passwordId',
    'passwordMachineName',
    'passwordName',
    'passwordNotes',
    'passwordUsername'
];
const croppedTextMaxLength = 40;

export const SearchResult = ({ rows, searchString = '', hasDoneASearch, totalCount = 0, currentCount = 0, resetLocal, onFetch }: IProps) => {
    const { translation } = useTypedTranslation();

    const [rowsPrepared, setRowsPrepared] = useState<IVaultDetail[]>([]);

    const modalSwitchState: boolean = useSelector((state: any) => state.modal);
    const [modalData, setModalData] = useState<IModalData>({
        headerTitle: '',
        modalSwitcher: modalSwitchState
    });

    const dispatch = useDispatch();

    const fetchSearchData = (skipCount: number, isReset: boolean) => {
        onFetch(skipCount, searchString, isReset);
    };

    useEffect(() => {
        if (rows.length) {
            handlePrepareRows(rows);
        } else {
            setRowsPrepared([]);
        }
    }, [rows]);

    const handlePrepareRows = (rows: VaultSearchItem[]) => {
        const searchStringLowerCase = searchString.toLowerCase();
        const vaultIdSet = new Set();
        const vaultsFiltered = rows.filter((obj) => {
            if (!vaultIdSet.has(obj.vaultId)) {
                vaultIdSet.add(obj.vaultId);
                return true;
            }

            return false;
        });

        let allPasswords: IVaultDetail[] = [];

        vaultsFiltered.forEach((vault) => {
            const pwdData: IVaultDetail = {} as IVaultDetail;
            pwdData.vaultId = vault.vaultId;
            pwdData.vaultName = vault.vaultName;
            pwdData.vaultOwnerId = vault.vaultOwnerId;
            pwdData.folders = [];

            const folders = rows.filter((folder: VaultSearchItem) => {
                return folder.vaultId && folder.folderId && folder.vaultId === vault.vaultId;
            });

            if (folders.length) {
                let seen: string[] = [];

                folders.forEach((itemFolder: VaultSearchItem, index: number) => {
                    if (!seen.includes(itemFolder.folderId)) {
                        pwdData.folders[index] = {} as IVaultsFolderData;
                        pwdData.folders[index].folderName = itemFolder.folderName;
                        pwdData.folders[index].vaultFolderId = itemFolder.folderId;

                        const passwordsInFolder = rows.filter((password: VaultSearchItem) => {
                            return password.folderId && password.passwordId && password.folderId === itemFolder.folderId;
                        });

                        if (passwordsInFolder.length) {
                            pwdData.folders[index].passwords = [];
                            passwordsInFolder.forEach((passwordInFolder: VaultSearchItem, index2: number) => {
                                pwdData.folders[index].passwords[index2] = {} as VaultSearchItem;
                                pwdData.folders[index].passwords[index2].passwordName = passwordInFolder.passwordName;
                                pwdData.folders[index].passwords[index2].passwordId = passwordInFolder.passwordId;
                                pwdData.folders[index].passwords[index2].whereFound = [];
                                fieldsToFindSearched.forEach((field: string) => {
                                    const text = passwordInFolder[field as keyof VaultSearchItem] as string;
                                    const textLowerCase = text?.toLowerCase();

                                    if (textLowerCase && textLowerCase?.includes(searchStringLowerCase)) {
                                        const croppedText =
                                            text.substring(0, croppedTextMaxLength) + (text.length > croppedTextMaxLength ? '...' : '');

                                        pwdData.folders[index].passwords[index2].whereFound?.push({ [field]: croppedText });
                                    }
                                });
                            });
                        }

                        seen.push(itemFolder.folderId);
                    }
                });
            }

            allPasswords.push(pwdData);
        });

        setRowsPrepared(allPasswords);
    };

    const handleRedirect = (id: string) => () => {
        let url: string = `/passwords/details/${id}`;

        basicService.redirect(url);
    };

    const handleRedirectFromFolder = (vaultId: string, vaultFolderId: string, folderName: string) => () => {
        let encodedFolderName = encodeURIComponent(folderName);
        let url: string = `/passwords/details/${vaultId}?folderId=${vaultFolderId}&folderName=${encodedFolderName}`;

        basicService.redirect(url);
    };

    const openPasswordDetailsModal = (passwordName: string, passwordId: string, vaultId: string, vaultName: string, isIndividual: boolean) => () => {
        dispatch(getModalSwitch(true));
        setModalData({
            headerTitle: passwordName,
            modalSwitcher: modalSwitchState,
            form: <PasswordDetailsDialog passwordId={passwordId} vaultId={vaultId} vaultName={vaultName} isIndividual={isIndividual} />
        });
    };

    const getWhereToFound = (rowPwd: VaultSearchItem) => {
        return rowPwd.whereFound?.length
            ? rowPwd.whereFound.map((item, index) => (
                  <div key={index} className='passwordVaults__vaultTitle--passwords-lists-where-found'>
                      {Object.keys(item).map((fieldName) => (
                          <div key={fieldName}>
                              {`${translation(fieldName.toUpperCase() as TranslationsTypes)}: ${item[fieldName as keyof WhereFoundItem]}`}
                          </div>
                      ))}
                  </div>
              ))
            : null;
    };

    return (
        <div>
            <ModalComponent {...modalData} />

            {hasDoneASearch && !rows.length ? (
                <EmptyTable primaryText={'NO_RESULTS'} secondaryText={'NO_RESULTS_DETAIL'} displayType={EmptyTableType.emptySearch} />
            ) : (
                <>
                    {searchString && searchString.length > 0 && (
                        <div className='passwordVaults__no-results'>
                            <b>{translation('SEARCH_RESULTS_FOR') + ': "' + searchString + '"'}</b>
                        </div>
                    )}
                    <PasslyInfiniteScroll totalCount={totalCount} currentCount={currentCount} onFetch={fetchSearchData} resetLocal={resetLocal}>
                        <>
                            {rowsPrepared?.length > 0 && (
                                <>
                                    <div className='passwordVaults'>
                                        <div className='passwordVaults__column passwordVaults__column--lists'>
                                            {rowsPrepared.map((rowSearch: IVaultDetail, index: number) => (
                                                <div
                                                    key={rowSearch.vaultId + index}
                                                    className='passwordVaults__column-tile passwordVaults__column-tile--lists'
                                                >
                                                    {rowSearch.vaultOwnerId ? (
                                                        <span>
                                                            <ScreenLockPortraitIcon color='primary' fontSize='small' />
                                                        </span>
                                                    ) : (
                                                        <span>
                                                            <VpnLockIcon color='primary' fontSize='small' />
                                                        </span>
                                                    )}

                                                    <span className='passwordVaults__vaultTitle--lists'>
                                                        <span className='passwordVaults__vaultTitle' onClick={handleRedirect(rowSearch.vaultId)}>
                                                            {rowSearch.vaultName}
                                                        </span>
                                                        <br />
                                                        {rowSearch.folders?.length
                                                            ? rowSearch.folders.map((rowFolder: IVaultsFolderData) => (
                                                                  <div
                                                                      key={rowFolder.vaultFolderId + rowFolder.folderName}
                                                                      className='passwordVaults__vaultTitle--passwords-lists-sublists'
                                                                  >
                                                                      <div
                                                                          className='passwordVaults__vaultTitle--passwords-lists-folders'
                                                                          onClick={handleRedirectFromFolder(
                                                                              rowSearch.vaultId,
                                                                              rowFolder.vaultFolderId,
                                                                              rowFolder.folderName
                                                                          )}
                                                                      >
                                                                          <FolderOutlined fontSize='small' />
                                                                          {rowFolder.folderName}
                                                                      </div>
                                                                      {rowFolder.passwords?.length ? (
                                                                          <>
                                                                              {rowFolder.passwords.length &&
                                                                                  rowFolder.passwords.map((rowPwd: VaultSearchItem) => (
                                                                                      <div key={rowPwd.passwordId}>
                                                                                          <div className='passwordVaults__vaultTitle--passwords-lists-passwords'>
                                                                                              <PasslyButton
                                                                                                  id='clickOnPwd'
                                                                                                  onClick={openPasswordDetailsModal(
                                                                                                      rowPwd.passwordName,
                                                                                                      rowPwd.passwordId,
                                                                                                      rowSearch.vaultId,
                                                                                                      rowSearch.vaultName,
                                                                                                      rowSearch.vaultOwnerId
                                                                                                  )}
                                                                                              >
                                                                                                  <VpnKeyIcon color='secondary' fontSize='small' />
                                                                                                  {rowPwd.passwordName}
                                                                                              </PasslyButton>
                                                                                          </div>
                                                                                          {getWhereToFound(rowPwd)}
                                                                                      </div>
                                                                                  ))}
                                                                          </>
                                                                      ) : null}
                                                                  </div>
                                                              ))
                                                            : null}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </>
                            )}
                        </>
                    </PasslyInfiniteScroll>
                </>
            )}
        </div>
    );
};
